import { useUserValidationConstraints } from '../../components/Registration/const';
import { FormField, FormFieldType } from '../../components/shared/Form';
import { Icon } from '../../components/shared/Icon';
import { countryCodeOptions } from '../../components/shared/TelephoneInput/countryCodes';
import { useTranslations } from '../useTranslationsHelper';
import { getPromoCodeParam } from './useRegistrationStepper.helper';
import { FlattenedProfileData } from './useRegistrationStepper.model';

export type RegistrationFieldMap = Record<string, FormField<Partial<FlattenedProfileData>>>;
export const useRegistrationFieldConfig = (): RegistrationFieldMap => {
  const { t } = useTranslations();
  const userValidationConstraints = useUserValidationConstraints();

  const { promoValue, promoDisabled } = getPromoCodeParam();

  return {
    password: {
      type: FormFieldType.PasswordInput,
      name: 'password',
      label: t('input.password.label'),
      rules: userValidationConstraints.password.rules as never,
      props: {
        autoComplete: 'new-password',
        showRequirements: true,
      },
    },
    email: {
      type: FormFieldType.TextInput,
      name: 'email',
      label: t('input.mail.label'),
      rules: userValidationConstraints.email.rules as never,
      props: {
        autoComplete: 'email',
        type: 'email',
        autoCapitalize: 'off',
        inputMode: 'email',
      },
    },
    givenName: {
      type: FormFieldType.TextInput,
      name: 'givenName',
      label: t('register.steps.personal.first-name-input.label'),
      rules: {
        maxLength: {
          value: 40,
          message: t('validation.first-name.less-symbols'),
        },
        minLength: {
          value: 2,
          message: t('validation.first-name.more-symbols'),
        },
        required: t('validation.field-required'),
        pattern: {
          value: userValidationConstraints.lastnamePattern,
          message: t('validation.first-name.allowed'),
        },
      },
      props: { autoComplete: 'given-name', autoCorrect: 'off' },
    },
    familyName: {
      type: FormFieldType.TextInput,
      name: 'familyName',
      label: t('register.steps.personal.last-name-input.label'),
      rules: {
        maxLength: {
          value: 40,
          message: t('validation.last-name.less-symbols'),
        },
        minLength: {
          value: 2,
          message: t('validation.last-name.more-symbols'),
        },
        required: t('validation.field-required'),
        pattern: {
          value: userValidationConstraints.lastnamePattern,
          message: t('validation.last-name.allowed'),
        },
      },
      props: { autoComplete: 'family-name', autoCorrect: 'off' },
    },
    birthDate: {
      type: FormFieldType.DateOfBirthInput,
      name: 'birthDate',
      label: t('register.steps.personal.dof-input.label'),
      placeholder: 'DD/MM/YYYY',
      dateMask: '99/99/9999',
      dateFormat: 'dd/MM/YYYY',
      rules: userValidationConstraints.dateOfBirth.rules,
      props: { autoComplete: 'bday', type: 'text', inputMode: 'numeric' },
    },
    honorificPrefix: {
      type: FormFieldType.Select,
      name: 'honorificPrefix',
      label: t('register.steps.personal.honor-input.label'),
      rules: {},
      defaultValue: 'Mr',
      options: [
        { value: 'Mr', label: t('register.steps.personal.mr') },
        { value: 'Mrs', label: t('register.steps.personal.mrs') },
        { value: 'Ms', label: t('register.steps.personal.ms') },
        { value: 'Other', label: t('register.steps.personal.other') },
      ],
    },
    addrStreetAddress: {
      type: FormFieldType.TextInput,
      name: 'addrStreetAddress',
      label: t('registration.step-three.address.label'),
      rules: {
        required: t('validation.required'),
        pattern: {
          value: userValidationConstraints.addressLinePattern,
          message: t('validation.address.invalid'),
        },
      },
    },
    addrLocality: {
      type: FormFieldType.TextInput,
      name: 'addrLocality',
      label: t('registration.step-three.city.label'),
      rules: {
        required: t('validation.required'),
        pattern: {
          value: userValidationConstraints.cityLinePattern,
          message: t('validation.city.invalid'),
        },
      },
    },
    addrPostalCode: {
      type: FormFieldType.NoWhitespaceInput,
      name: 'addrPostalCode',
      label: 'Enter your postcode',
      rules: {
        required: t('validation.required'),
        pattern: {
          value: userValidationConstraints.postalCodePattern,
          message: 'Your postcode is invalid',
        },
        validate: {
          noNorthernIreland: (value: string): string | boolean => {
            return !value.toUpperCase().startsWith('BT') || 'We do not allow Northern Ireland addresses';
          },
        },
      },
    },
    promoCode: {
      type: FormFieldType.TextInput,
      props: {
        defaultValue: promoValue,
      },
      name: 'promoCode',
      label: t('register.step.address.promo-code.label'),
      rules: {
        pattern: {
          value: userValidationConstraints.promoCodePattern,
          message: t('validation.promo-code.invalid'),
        },
      },
      disabled: promoDisabled,
    },
    mobileNumber: {
      type: FormFieldType.TelephoneInput,
      label: t('register.step.address.phone-number.label'),
      name: 'mobileNumber',
      rules: userValidationConstraints.telephone,
      props: {
        maskedInputProps: {
          name: 'countryCode',
          label: t('input.country-code'),
          mask: '',
          autoComplete: 'tel-national',
          type: 'tel',
        },
        selectProps: {
          label: t('input.country-code'),
          name: 'mobilePrefix',
          options: countryCodeOptions,
        },
      },
    },
    acceptTerms: {
      type: FormFieldType.Checkbox,
      name: 'acceptTerms',
      label: 'InnerHTML',
      rules: {
        required: t('validation.required'),
        validate: {
          accepted: (v) => v === true,
        },
      },
    },
    acceptMarketingPreferences: {
      type: FormFieldType.Checkbox,
      name: 'acceptMarketingPreferences',
      label: 'InnerHTML',
    },
    toggleAllMarketing: {
      type: FormFieldType.Switch,
      name: 'toggleAllMarketing',
      label: t('my-account.home.settings.communication.all'),
    },
    casinoHeading: {
      type: FormFieldType.Heading,
      name: 'casinoHeading',
      label: 'Casino',
    },
    sportHeading: {
      type: FormFieldType.Heading,
      name: 'sportHeading',
      label: 'Sports',
    },
    marketingConsentsParagraph: {
      type: FormFieldType.Paragraph,
      name: 'marketingConsentsParagraph',
      label:
        'Choose how you’d like to be notified about personalised promotions, free bets, exclusive offers, and Betzone updates.',
    },
    // Casino marketing preferences
    optInEmailCasino: {
      type: FormFieldType.Switch,
      name: 'optInEmailCasino',
      label: 'Email',
      props: {
        iconLabel: <Icon variant="Email" />,
      },
    },
    optInSmsCasino: {
      type: FormFieldType.Switch,
      name: 'optInSmsCasino',
      label: 'SMS',
      props: {
        iconLabel: <Icon variant="Sms" />,
      },
    },
    optInTelephoneCasino: {
      type: FormFieldType.Switch,
      name: 'optInTelephoneCasino',
      label: 'Phone',
      props: {
        iconLabel: <Icon variant="Phone" />,
      },
    },
    // Sports marketing preferences
    optInEmailSport: {
      type: FormFieldType.Switch,
      name: 'optInEmailSport',
      label: 'Email',
      props: {
        iconLabel: <Icon variant="Email" />,
      },
    },
    optInSmsSport: {
      type: FormFieldType.Switch,
      name: 'optInSmsSport',
      label: 'SMS',
      props: {
        iconLabel: <Icon variant="Sms" />,
      },
    },
    optInTelephoneSport: {
      type: FormFieldType.Switch,
      name: 'optInTelephoneSport',
      label: 'Phone',
      props: {
        iconLabel: <Icon variant="Phone" />,
      },
    },
  };
};
